import React from 'react';
import Select, { components, SingleValueProps, OptionProps, StylesConfig, GroupBase } from 'react-select';
import { cn } from '../lib/utils';
import { customSelectStyles } from '../shared/constants';

interface StatusOption {
    label: string;
    value: string;
    color: string;
}

interface StatusSelectProps {
    options: StatusOption[];
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    className?: string;
}

const StatusDot = ({ color, isActive }: { color: string; isActive?: boolean }) => {
    const bgColor = {
        green: 'bg-green-500',
        red: 'bg-red-500',
        yellow: 'bg-yellow-500',
        blue: 'bg-blue-500',
        gray: 'bg-gray-400',
    }[color] || 'bg-gray-400';

    const ringColor = {
        green: 'ring-green-400/30',
        red: 'ring-red-400/30',
        yellow: 'ring-yellow-400/30',
        blue: 'ring-blue-400/30',
        gray: 'ring-gray-400/30',
    }[color] || 'ring-gray-400/30';

    return (
        <div className="relative flex items-center justify-center mr-3 ml-1 translate-y-[0.5px]">
            {isActive && (
                <div className={cn("absolute w-3 h-3 rounded-full animate-pulse opacity-75", bgColor)} />
            )}
            <div className={cn("relative w-2 h-2 rounded-full ring-[3px]", bgColor, ringColor)} />
        </div>
    );
};

const CustomSingleValue = (props: SingleValueProps<StatusOption>) => (
    <components.SingleValue {...props}>
        <div className="flex items-center">
            <StatusDot color={props.data.color} isActive={props.data.value === 'ENABLED' || props.data.value === 'ACTIVE'} />
            <span className="font-bold text-[11px] uppercase tracking-wider text-gray-700">
                {props.data.label}
            </span>
        </div>
    </components.SingleValue>
);

const CustomOption = (props: OptionProps<StatusOption>) => (
    <components.Option {...props}>
        <div className="flex items-center">
            <StatusDot color={props.data.color} />
            <span className={cn(
                "font-bold text-[11px] uppercase tracking-wider",
                props.isSelected ? "text-gray-900" : "text-gray-500"
            )}>
                {props.data.label}
            </span>
        </div>
    </components.Option>
);

const StatusSelect: React.FC<StatusSelectProps> = ({ options, value, onChange, disabled, className }) => {
    const selectedOption = options.find(opt => opt.value === value);

    const selectStyles: StylesConfig<StatusOption, false, GroupBase<StatusOption>> = {
        ...customSelectStyles<StatusOption>(),
        control: (base, state) => ({
            ...base,
            minHeight: '32px',
            height: '32px',
            width: '140px',
            backgroundColor: 'white',
            borderRadius: '9999px',
            border: state.isFocused ? '1px solid #e5e7eb' : '1px solid #f3f4f6',
            boxShadow: state.isFocused ? '0 4px 12px rgba(0,0,0,0.05)' : 'none',
            cursor: 'pointer',
            padding: '0 4px',
            '&:hover': {
                borderColor: '#e5e7eb',
                backgroundColor: '#f9fafb',
            }
        }),
        valueContainer: (base) => ({
            ...base,
            padding: '0 10px',
        }),
        dropdownIndicator: (base) => ({
            ...base,
            padding: '0 4px',
            color: '#9ca3af',
        }),
        indicatorSeparator: () => ({ display: 'none' }),
        menu: (base) => ({
            ...base,
            borderRadius: '12px',
            padding: '4px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
            border: '1px solid #f3f4f6',
            width: '160px',
            marginTop: '8px',
            overflow: 'hidden',
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? '#f9fafb' : state.isFocused ? '#f9fafb' : 'transparent',
            borderRadius: '8px',
            margin: '2px 0',
            cursor: 'pointer',
            padding: '6px 12px',
            '&:active': {
                backgroundColor: '#f3f4f6',
            }
        }),
    };

    return (
        <div className={cn("status-select-container", className)}>
            <Select<StatusOption, false>
                options={options}
                value={selectedOption}
                onChange={(newValue) => newValue && onChange(newValue.value)}
                isSearchable={false}
                isDisabled={disabled}
                components={{
                    SingleValue: CustomSingleValue,
                    Option: CustomOption,
                }}
                styles={selectStyles}
            />
        </div>
    );
};

export default StatusSelect;
